import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';

import { ConsumptionEntry } from './ConsumptionEntry';
import { DirectDebitSavings } from './DirectDebitSavings';
import { EnergySavingFact } from 'components/common/energy-saving-fact/EnergySavingFact';
import { PrepayOnlyMessage } from './PrepayOnlyMessage';

import { useMeterType } from 'hooks/useMeterType';
import { useNavigation } from 'hooks/useNavigation';
import { useEnergyNeedsScreen } from '../hooks/useEnergyNeedsScreen';
import { useHasInitialState } from '../hooks/useHasInitialState';
import { useConsumptionEntry } from '../hooks/useConsumptionEntry';

import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import {
  setElectric,
  setGas,
  setGasElectric
} from 'state/fuel-type/fuel-type-slice';
import { setPaymentMethod } from 'state/payment-method/payment-method-slice';
import {
  selectElectricVehicle,
  setElectricVehicle
} from 'state/electric-vehicle/electric-vehicle-slice';
import { setPersonaliseQuoteShowFuel } from 'state/settings/settings-slice';
import { setValidationRequired } from 'state/meterpoint-validation/meterpoint-validation-slice';

import { stringToBool } from 'utils/misc';
import { FUEL_OPTIONS } from 'config/app-config';

export const testId = 'energyNeedsFormTestId';

export const EnergyNeedsForm = () => {
  const { isEnergyUsageExceeds } = useConsumptionEntry();
  const [key, setKey] = useState(uuidv4());
  const dispatch = useDispatch();
  const { eventTypes, next } = useNavigation();
  const {
    hasElectricityMeter,
    hasGasMeter,
    isRelatedElectricityMeter
  } = useMeterType();
  const {
    fuel: hasFuelInitialState,
    ev: hasEVInitialState
  } = useHasInitialState();

  const {
    paymentMethodValue,
    displayedPaymentOptions,
    defaultPaymentMethod,
    hideFuelType,
    hideEV,
    hidePaymentMethod,
    hideConsumption,
    isPrePayOnly
  } = useEnergyNeedsScreen();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const fuelValue = getFuelValue(fuelType);
  const electricVehicle = useSelector(selectElectricVehicle, shallowEqual);

  const handleChangeFuelType = (event) => {
    const {
      target: { value }
    } = event;

    switch (value) {
      case FUEL_OPTIONS.ELECTRIC:
        dispatch(setElectric());
        dispatch(
          setValidationRequired({
            electricity: !hasElectricityMeter && !isRelatedElectricityMeter,
            gas: false
          })
        );
        break;
      case FUEL_OPTIONS.GAS:
        dispatch(setGas());
        dispatch(setElectricVehicle(false));
        dispatch(
          setValidationRequired({
            electricity: false,
            gas: !hasGasMeter
          })
        );
        break;
      default:
        dispatch(setGasElectric());
        dispatch(
          setValidationRequired({
            electricity: !hasElectricityMeter && !isRelatedElectricityMeter,
            gas: !hasGasMeter
          })
        );
    }

    setKey(uuidv4());
  };

  const handleChangePaymentMethod = (event) => {
    const {
      target: { value }
    } = event;

    dispatch(setPaymentMethod(value));
    dispatch(setElectricVehicle(false));
    setKey(uuidv4());
  };

  const handleChangeEV = (event) => {
    const {
      target: { value }
    } = event;
    const boolValue = stringToBool(value);

    dispatch(setElectricVehicle(boolValue));
  };

  const handleGoNext = () => {
    next({ eventType: eventTypes.NEXT });
    dispatch(setPersonaliseQuoteShowFuel(false));
  };

  useEffect(() => {
    dispatch(setPaymentMethod(defaultPaymentMethod));

    setKey(uuidv4()); // refresh the radio input. bug in Nucleus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelType]);

  useEffect(() => {
    if (hasFuelInitialState) {
      dispatch(setGasElectric());
      dispatch(
        setValidationRequired({
          electricity: !isRelatedElectricityMeter && !hasElectricityMeter,
          gas: !hasGasMeter
        })
      );
    } else {
      dispatch(
        setValidationRequired({
          electricity:
            !!fuelType.electric &&
            !isRelatedElectricityMeter &&
            !hasElectricityMeter,
          gas: !!fuelType.gas && !hasGasMeter
        })
      );
    }

    hasEVInitialState && dispatch(setElectricVehicle(false));

    setKey(uuidv4()); // refresh the radio input. bug in Nucleus

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={testId}>
      <NsForm onSubmit={handleGoNext}>
        <PrepayOnlyMessage fuelType={fuelType} shouldShow={isPrePayOnly} />
        {!hideFuelType && (
          <NsRadioInput
            key={`fuelType${key}`}
            heading="What fuel do you need?"
            name="fuelType"
            onChange={handleChangeFuelType}
            value={fuelValue}
            className="inline"
            options={[
              {
                label: 'Gas & electricity',
                value: FUEL_OPTIONS.DUAL
              },
              {
                label: 'Electricity only',
                value: FUEL_OPTIONS.ELECTRIC
              },
              {
                label: 'Gas only',
                value: FUEL_OPTIONS.GAS
              }
            ]}
          />
        )}
        {!hidePaymentMethod && (
          <>
            <NsRadioInput
              key={`paymentMethod${key}`}
              heading="How would you like to pay?"
              name="paymentMethod"
              dataTestId="radioPaymentMethodTestId"
              onChange={handleChangePaymentMethod}
              value={paymentMethodValue}
              className="inline"
              options={displayedPaymentOptions}
            />
            <DirectDebitSavings paymentMethod={paymentMethodValue} />
          </>
        )}
        {!hideConsumption && <ConsumptionEntry />}
        {!hideEV && (
          <NsRadioInput
            key={`electricVehicle${key}`}
            heading="Would you like an electric vehicle tariff?"
            name="electricVehicle"
            dataTestId="radioElectricVehicleTestId"
            onChange={handleChangeEV}
            value={electricVehicle}
            className="inline"
            options={[
              {
                label: 'Yes',
                value: 'true'
              },
              {
                label: 'No',
                value: 'false'
              }
            ]}
          />
        )}

        <NsPrimaryCta>
          {isEnergyUsageExceeds && <>I&apos;m happy to continue</>}
          {!isEnergyUsageExceeds && <>Continue</>}
        </NsPrimaryCta>
        <EnergySavingFact fact="Get half-price electricity every Sunday with PeakSave and you could save £24 a year on average." />
      </NsForm>
    </div>
  );
};

export default EnergyNeedsForm;
